import React, { useEffect, useState, useCallback } from 'react';
import { PrivyProvider as BasePrivyProvider } from '@privy-io/react-auth';

interface PrivyProviderProps {
  children: React.ReactNode;
}

// Error boundary component for Privy initialization
class PrivyErrorBoundary extends React.Component<
  { children: React.ReactNode; onError: (error: Error) => void },
  { hasError: boolean; error: Error | null }
> {
  constructor(props: { children: React.ReactNode; onError: (error: Error) => void }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Privy Provider Error:', error, errorInfo);
    this.props.onError(error);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          padding: '20px', 
          backgroundColor: '#f44336', 
          color: 'white', 
          textAlign: 'center' 
        }}>
          <h3>Authentication Error</h3>
          <p>There was an issue initializing the authentication system.</p>
          <p>Error: {this.state.error?.message}</p>
          <button 
            onClick={() => window.location.reload()} 
            style={{ 
              padding: '10px 20px', 
              backgroundColor: '#fff', 
              color: '#f44336', 
              border: 'none', 
              borderRadius: '4px', 
              cursor: 'pointer',
              marginTop: '10px'
            }}
          >
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

export default function PrivyProvider({ children }: PrivyProviderProps) {
  const [isReady, setIsReady] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const appId = import.meta.env.VITE_PRIVY_APP_ID;

  const handlePrivyError = useCallback((error: Error) => {
    console.error('Privy initialization error:', error);
    setInitError(error.message);
  }, []);

  // Inject styles to ensure Privy modal works properly
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Ensure Privy modals have proper z-index */
      [data-privy-modal] {
        z-index: 10000 !important;
      }
      
      /* Ensure Privy iframe has proper layering */
      iframe[src*="privy.io"] {
        z-index: 10001 !important;
        pointer-events: auto !important;
      }
      
      /* Ensure modal inputs are focusable */
      [data-privy-modal] input,
      [data-privy-modal] textarea,
      [data-privy-modal] button {
        pointer-events: auto !important;
        position: relative !important;
      }
      
      /* Ensure modal content is interactive */
      [data-privy-modal] * {
        pointer-events: auto !important;
      }
      
      /* Fix focus issues */
      [data-privy-modal] input:focus,
      [data-privy-modal] textarea:focus {
        outline: 2px solid #9186D5 !important;
        outline-offset: 2px !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // Delayed initialization to prevent React hydration conflicts
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsReady(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  if (!appId) {
    console.error('VITE_PRIVY_APP_ID environment variable is not set');
    return (
      <div style={{ 
        padding: '20px', 
        backgroundColor: '#f44336', 
        color: 'white', 
        textAlign: 'center' 
      }}>
        <h3>Configuration Error</h3>
        <p>Please set VITE_PRIVY_APP_ID in your environment variables.</p>
        <p>Get your App ID from <a href="https://dashboard.privy.io" target="_blank" rel="noopener noreferrer" style={{ color: '#ffeb3b' }}>https://dashboard.privy.io</a></p>
      </div>
    );
  }

  if (initError) {
    return (
      <div style={{ 
        padding: '20px', 
        backgroundColor: '#f44336', 
        color: 'white', 
        textAlign: 'center' 
      }}>
        <h3>Authentication Initialization Error</h3>
        <p>{initError}</p>
        <button 
          onClick={() => {
            setInitError(null);
            setIsReady(false);
            setTimeout(() => setIsReady(true), 100);
          }}
          style={{ 
            padding: '10px 20px', 
            backgroundColor: '#fff', 
            color: '#f44336', 
            border: 'none', 
            borderRadius: '4px', 
            cursor: 'pointer',
            marginTop: '10px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!isReady) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#1a1a1a',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            border: '3px solid rgba(255,255,255,0.2)', 
            borderTop: '3px solid #9186D5', 
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 15px'
          }} />
          <p>Initializing authentication...</p>
        </div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  return (
    <PrivyErrorBoundary onError={handlePrivyError}>
      <BasePrivyProvider 
        appId={appId} 
        config={{
          // Appearance configuration
          appearance: {
            theme: 'dark',
            accentColor: '#9186D5',
            logo: '/privy.png',
            landingHeader: 'Welcome to Sphere',
            loginMessage: 'Create an account via Privy to get started'
          },
          // Login methods - Email and SMS for Privy users
          loginMethods: ['email', 'sms'],
          // Embedded wallets configuration
          embeddedWallets: {
            createOnLogin: 'users-without-wallets',
            requireUserPasswordOnCreate: false,
            showWalletUIs: true,
          },
        }}
      >
        {children}
      </BasePrivyProvider>
    </PrivyErrorBoundary>
  );
} 